import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import { RoleFilterProvider } from "./lib/roleFilter";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import EventsList from "./pages/EventsList";
import EventDetail from "./pages/EventDetail";
import AuthCallback from "./pages/AuthCallback";
import MagicCallback from "./pages/MagicCallback";
import PublicRSVP from "./pages/PublicRSVP";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Venues from "./pages/Venues";
import CalendarPage from "./pages/CalendarPage";
import CommandMode from "./pages/CommandMode";
import GuestCheckInKiosk from "./pages/GuestCheckInKiosk";
import InvitePage from "./pages/InvitePage";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F8]">
        <div className="eyebrow text-[#71717A]">Loading…</div>
      </div>
    );
  }
  if (!user) {
    // Remember where the (vendor / coordinator / client) was heading so we can
    // send them straight there after Google OAuth completes.
    try {
      const target = `${location.pathname}${location.search || ""}`;
      if (target && target !== "/" && !target.startsWith("/auth/")) {
        sessionStorage.setItem("post_login_return", target);
      }
    } catch (e) { console.debug("post_login_return set failed", e); }
    return <Navigate to="/" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F8]">
        <div className="eyebrow text-[#71717A]">Loading…</div>
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback anywhere in the app
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/rsvp/:eventId" element={<PublicRSVP />} />
      <Route path="/invite/:eventId" element={<InvitePage />} />
      <Route path="/" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic" element={<MagicCallback />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventsList /></ProtectedRoute>} />
      <Route path="/events/:id" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
      <Route path="/events/:id/command" element={<ProtectedRoute><CommandMode /></ProtectedRoute>} />
      <Route path="/checkin/:eventId" element={<GuestCheckInKiosk />} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
      <Route path="/venues" element={<ProtectedRoute><Venues /></ProtectedRoute>} />
      <Route path="/venues/:id" element={<ProtectedRoute><Venues /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <RoleFilterProvider>
              <AppRouter />
              <Toaster position="top-right" richColors closeButton />
            </RoleFilterProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}
